import React, { useState } from 'react';
import Draggable from 'react-draggable';
import AIReadingLevelButton from '@/components/shared/TextControlsToolbar/AIReadingLevelButton';
import TextToSpeechButton from '@/components/shared/TextToSpeechButton';
import TranslateButton from '@/components/shared/TextControlsToolbar/TranslateButton/TranslateButton';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import useReadingDraggableToolbar from '../hooks/useReadingDraggableToolbar';
import { toggleMainIdeas } from '../readingSectionUtils';

interface DraggableToolbarProps {
  hasMainIdeas: boolean;
  hasReadingLevelB?: boolean;
  isPresentMode?: boolean;
  isStaffer?: boolean;
  locale: Locale;
  /** Currently selected reading level — controlled from SlideObjectReadingSection. */
  onReadingLevelSelect?: (level: string) => void;
  programGradeBands?: string[];
  programId?: number;
  readingLevel?: string;
  readingLevelAEnabled?: boolean;
  sectionId: number;
  showAIReadingLevelsForSection?: boolean;
  studentLeveledReading?: string | null;
}

// AIDEV-NOTE: Reading toolbar with TTS, Translate, and Main Ideas tools.
// Draggable in present mode, fixed position otherwise.
// Position is persisted to localStorage via useReadingDraggableToolbar.
const DraggableToolbar = ({
  hasMainIdeas,
  hasReadingLevelB,
  isPresentMode = false,
  isStaffer = false,
  locale,
  onReadingLevelSelect,
  programGradeBands,
  programId,
  readingLevel: readingLevelProp,
  readingLevelAEnabled,
  sectionId,
  showAIReadingLevelsForSection,
  studentLeveledReading = null,
}: DraggableToolbarProps) => {
  const { disabled, nodeRef, onStop, position, scale } = useReadingDraggableToolbar();
  const [mainIdeasOpen, setMainIdeasOpen] = useState(false);

  // AIDEV-NOTE: PLAT-16897 — when onReadingLevelSelect is provided, readingLevel is controlled
  // by the parent (SlideObjectReadingSection). Fall back to local state for standalone usage.
  const [localReadingLevel, setLocalReadingLevel] = useState<string>('a');
  const readingLevel = readingLevelProp ?? localReadingLevel;
  const handleReadingLevelSelect = onReadingLevelSelect ?? setLocalReadingLevel;

  return (
    <Draggable
      bounds=".slide"
      disabled={disabled}
      handle=".handler"
      nodeRef={nodeRef}
      position={position}
      scale={scale}
      onStop={onStop}
    >
      {/* tw-fixed in builder; tw-absolute when portaled into .slide for present mode */}
      <div
        ref={nodeRef}
        className={cn(
          'tw-z-10 tw-flex tw-items-center tw-w-auto',
          'tw-border tw-border-solid tw-border-white tw-rounded-lg tw-p-1',
          'tw-bg-gradient-to-r tw-from-[#6438AF] tw-to-[#45257E] tw-shadow-md tw-gap-0.5',
          '[&_img]:tw-h-[20px]',
          '[&_button]:tw-bg-white/10 [&_button]:tw-border-0',
          '[&_button_i]:tw-text-white',
          (isPresentMode ? 'tw-absolute tw-top-0 tw-left-0' : 'tw-fixed'),
        )}
      >
        <div
          className={cn(
            'handler tw-cursor-pointer tw-flex tw-w-5 tw-h-full tw-justify-center tw-items-center',
            'tw-text-white tw-gap-0.5',
            { 'tw-cursor-move': isPresentMode },
          )}
        >
          <i className="fa fa-ellipsis-v" />
          <i className="fa fa-ellipsis-v" />
        </div>
        <AIReadingLevelButton
          isInDraggableToolbar
          availableGradeBands={hasReadingLevelB ? ['b'] : []}
          isStaffer={isStaffer}
          programGradeBands={programGradeBands}
          programId={programId}
          readingLevel={readingLevel}
          readingLevelAEnabled={readingLevelAEnabled}
          sectionId={sectionId}
          showAIReadingLevelsForSection={showAIReadingLevelsForSection}
          studentLeveledReading={studentLeveledReading}
          onSelect={handleReadingLevelSelect}
        />
        <TextToSpeechButton isInDraggableToolbar locale={locale} page={sectionId} />

        {/* Translations */}
        <TranslateButton
          isInDraggableToolbar
          iconFill="white"
          locale={locale}
          userLanguage={locale === 'es' ? 'Spanish' : 'English'}
        />
        {hasMainIdeas && (
          <button
            className={cn(
              'tw-flex tw-justify-center tw-items-center tw-rounded tw-border-none tw-cursor-pointer',
              'tw-h-8 tw-w-8',
              { '!tw-bg-white/100': mainIdeasOpen, 'tw-bg-white/10 hover:!tw-bg-white/30': !mainIdeasOpen },
            )}
            type="button"
            onClick={() => {
              toggleMainIdeas(mainIdeasOpen);
              setMainIdeasOpen(!mainIdeasOpen);
            }}
          >
            <Icon color={mainIdeasOpen ? '#522e91' : 'white'} icon="MainIdeas" size={20} />
          </button>
        )}
        <div
          data-draggable-toolbar
          className="[&_button]:tw-h-8 [&_button]:tw-w-8 [&_button:not(.purple):hover]:!tw-bg-white/30 [&_button.purple]:!tw-bg-white tw-mr-2"
          id="highlights-toggle"
          style={{ '--marker-body-color': 'white' } as React.CSSProperties}
        />
      </div>
    </Draggable>
  );
};

export default DraggableToolbar;
